import { useState, useEffect, useRef, useMemo } from "react";
import { config } from "../utils/config";
import { getCookie } from "../utils/cookieHandler";

/**
 * Hook to check if billing events are locked for various source types
 * This determines if related fields should be disabled in the UI
 * 
 * @param {object} fields - Current form fields data
 * @param {number} reservationId - Optional reservation ID
 * @returns {object} Lock status for charges, transactions, addons, time adjustments, and reservation level
 */
export const useBillingEventsLockStatus = (fields, reservationId = null) => {
  const [lockStatus, setLockStatus] = useState({
    charges: {},
    transactions: {},
    addons: {},
    timeAdjustments: {},
    customOrders: {},
    reservationLevel: false,
    rentalPricingTypes: {}, // Lock status by billing event type
    loading: true,
  });

  const fetchingRef = useRef(false);
  const lastFetchedIdsRef = useRef(null);

  // Extract IDs once to avoid recalculating on every render
  const extractedIds = useMemo(() => {
    const getFieldData = (fieldName) => {
      if (Array.isArray(fields)) {
        const field = fields.find(f => f.name === fieldName);
        return field?.defaultValue;
      }
      return fields?.[fieldName];
    };

    const chargeIds = [];
    const transactionIds = [];
    const addonIds = [];
    const timeAdjustmentIds = [];
    const customOrderIds = [];

    // Extract charge IDs
    const charges = getFieldData('charges');
    if (charges && Array.isArray(charges)) {
      charges.forEach(charge => {
        if (charge.id && !charge.delete) {
          chargeIds.push(charge.id);
        }
      });
    }

    // Extract transaction IDs (only Security Deposit Refund type)
    const transactions = getFieldData('transactions');
    if (transactions && Array.isArray(transactions)) {
      transactions.forEach(transaction => {
        if (transaction.id && !transaction.delete && transaction.type?.name === "Security Deposit Refund") {
          transactionIds.push(transaction.id);
        }
      });
    }

    // Extract addon IDs
    const addons = getFieldData('reservation_addons') || getFieldData('reservation_addons2');
    if (addons && Array.isArray(addons)) {
      addons.forEach(addon => {
        if (addon.id && !addon.delete) {
          addonIds.push(addon.id);
        }
      });
    }

    // Extract time adjustment IDs
    const timeExtensions = getFieldData('time_extension');
    if (timeExtensions && Array.isArray(timeExtensions)) {
      timeExtensions.forEach(timeAdj => {
        if (timeAdj.id && !timeAdj.delete) {
          timeAdjustmentIds.push(timeAdj.id);
        }
      });
    }

    // Extract custom order IDs
    const customOrders = getFieldData('custom_orders');
    if (customOrders && Array.isArray(customOrders)) {
      customOrders.forEach(order => {
        if (order.id && !order.delete) {
          customOrderIds.push(order.id);
        }
      });
    }

    return { chargeIds, transactionIds, addonIds, timeAdjustmentIds, customOrderIds };
  }, [fields]);

  useEffect(() => {
    // Create a stable key for comparison
    const idsKey = JSON.stringify({
      ...extractedIds,
      reservationId
    });

    // Skip if already fetching or if IDs haven't changed
    if (fetchingRef.current || lastFetchedIdsRef.current === idsKey) {
      return;
    }

    lastFetchedIdsRef.current = idsKey;
    fetchLockStatus();
  }, [extractedIds, reservationId]);

  const fetchLockStatus = async () => {
    if (!reservationId && !fields) {
      setLockStatus(prev => ({ ...prev, loading: false }));
      fetchingRef.current = false;
      return;
    }

    fetchingRef.current = true;

    try {
      const { chargeIds, transactionIds, addonIds, timeAdjustmentIds, customOrderIds } = extractedIds;

      console.log('[Lock Status] Fetching with IDs:', {
        chargeIds,
        transactionIds,
        addonIds,
        timeAdjustmentIds,
        customOrderIds,
        reservationId
      });

      // Skip API call if no IDs to check and no reservationId
      if (chargeIds.length === 0 && transactionIds.length === 0 && 
          addonIds.length === 0 && timeAdjustmentIds.length === 0 && 
          customOrderIds.length === 0 && !reservationId) {
        setLockStatus({
          charges: {},
          transactions: {},
          addons: {},
          timeAdjustments: {},
          customOrders: {},
          reservationLevel: false,
          rentalPricingTypes: {},
          loading: false,
        });
        fetchingRef.current = false;
        return;
      }

      // Call API to check lock status
      const response = await fetch(`${config.apiUrl}/api/billing-events/check-locked`, {
        method: "POST",
        headers: {
          Authorization: `Bearer ${getCookie("_AT")}`,
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          chargeIds,
          transactionIds,
          addonIds,
          timeAdjustmentIds,
          customOrderIds,
          reservationId: reservationId || null,
        }),
      });

      const result = await response.json();

      if (result.success) {
        setLockStatus({
          ...result.locked,
          loading: false,
        });
      } else {
        setLockStatus(prev => ({ ...prev, loading: false }));
      }
    } catch (error) {
      console.error("Error fetching billing events lock status:", error);
      setLockStatus(prev => ({ ...prev, loading: false }));
    } finally {
      fetchingRef.current = false;
    }
  };

  // Helper function to check if a specific item is locked
  const isLocked = (type, id) => {
    if (!id) return false;
    
    // Helper to check lock status (handles both boolean and object format)
    const checkLocked = (val) => {
      if (typeof val === 'boolean') return val;
      if (typeof val === 'object' && val !== null) return val.locked === true;
      return false;
    };
    
    switch (type) {
      case 'charge':
        return checkLocked(lockStatus.charges[id]);
      case 'transaction':
        return checkLocked(lockStatus.transactions[id]);
      case 'addon':
        return checkLocked(lockStatus.addons[id]);
      case 'timeAdjustment':
        return checkLocked(lockStatus.timeAdjustments[id]);
      case 'customOrder':
        return checkLocked(lockStatus.customOrders[id]);
      case 'reservation':
        return lockStatus.reservationLevel === true;
      case 'rentalPricingType':
        return checkLocked(lockStatus.rentalPricingTypes[id]);
      default:
        return false;
    }
  };

  return {
    lockStatus,
    isLocked,
    refreshLockStatus: fetchLockStatus,
  };
};

